/** App root: QueryClient, BrowserRouter, auth-gated routes */

import { useEffect, type ReactNode } from 'react'
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { useAuth } from './hooks/use-auth'
import { LoginPage } from './routes/login'
import { SettingsIndexRedirect, SettingsPage } from './routes/settings'
import { DocumentsPage } from './routes/documents'
import { DocumentRoute } from './routes/document'
import { ProjectsPage } from './routes/projects'
import { OnboardingPage } from './routes/onboarding'
import { SearchAnalyticsPage } from './routes/search-analytics'
import { GraphPage } from './routes/graph'
import { ShareView } from './routes/share-view'
import { PublishedView } from './routes/published-view'
import { StoragePage } from './routes/storage'
import { HostedSitesPage } from './routes/hosted-sites'
import { BillingPage } from './routes/billing'
import { SecretsMovedPage } from './routes/secrets-moved'
import { ProjectOverviewPage } from './routes/project-overview'
import { ProjectWikiPage } from './routes/project-wiki'
import { ProjectWikiPageView } from './routes/project-wiki-page'
import { ProjectSourcesPage } from './routes/project-sources'
import { ProjectSourceDetailPage } from './routes/project-source-detail'
import { ProjectReviewDetailPage } from './routes/project-review-detail'
import { ProjectHealthPage } from './routes/project-health'
import { ProjectAskPage } from './routes/project-ask'
import { AppShell, CreateProjectDialogHost, DOCS_URL } from './components/shell'
import { usePreferenceSync } from './hooks/use-preference-sync'
import { useDocumentPreferences } from './hooks/use-document-preferences'
import { useOnboardingStatus } from './hooks/use-onboarding'
import { usePreferences } from './hooks/use-preferences'
import { useProjects } from './hooks/use-projects'
import { DEFAULT_PROJECT_SLUG, resolveResumeProject } from './hooks/use-current-project'
import { useAppStore } from './stores/app-store'
import { useT } from './i18n'
import { projectPath } from './components/shell/nav-config'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
})

/** Wraps a route that requires authentication */
function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <FullScreenSpinner />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function FullScreenSpinner() {
  const t = useT()
  return (
    <div className="flex h-screen items-center justify-center bg-surface">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-accent" role="status" aria-label={t('common.loading')} />
    </div>
  )
}

/** Sends first-time users to /onboarding before the shell renders (waits for the lookup so nothing flashes) */
function OnboardingGate({ children }: { children: ReactNode }) {
  const status = useOnboardingStatus()
  if (status === 'loading') return <FullScreenSpinner />
  if (status === 'needed') return <Navigate to="/onboarding" replace />
  return <>{children}</>
}

/** /p → the project the user opened last (DB preference, then this browser), else the default project */
function ResumeProject() {
  const prefs = usePreferences()
  const projects = useProjects()
  const lastProjectSlug = useAppStore((s) => s.lastProjectSlug)
  if (projects.isError) return <Navigate to={projectPath(DEFAULT_PROJECT_SLUG)} replace />
  if (!projects.data || prefs.isLoading) return <FullScreenSpinner />
  return <Navigate to={projectPath(resolveResumeProject(projects.data, prefs.data?.last_project, lastProjectSlug))} replace />
}

/** In-app API/CLI docs moved to the docs site; old bookmarks are forwarded there */
function DocsRedirect() {
  const { pathname } = useLocation()
  const target = pathname.startsWith('/docs/cli') ? `${DOCS_URL}/04-cli-reference/` : `${DOCS_URL}/06-api-reference/`
  useEffect(() => {
    window.location.replace(target)
  }, [target])
  return <FullScreenSpinner />
}

/** Public routes never need the user's DB preferences (and must not probe /api/auth/me for them) */
const PUBLIC_PATH = /^\/(login|share\/|pub\/|docs\/)/

/** Stays mounted across authenticated navigations so debounced preference writes are not dropped */
function AuthenticatedPreferenceSync() {
  usePreferenceSync()
  return null
}

/** App-wide effects: theme class / color-scheme / <html lang>, DB preference sync, shared dialogs */
function AppChrome() {
  useDocumentPreferences()
  const { pathname } = useLocation()
  if (PUBLIC_PATH.test(pathname)) return null
  return (
    <>
      <AuthenticatedPreferenceSync />
      <CreateProjectDialogHost />
    </>
  )
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/pub/:slug" element={<PublishedView />} />
      <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
      {/* Profile moved into Settings; API/CLI docs moved to the docs site (old pages are kept in code, not routed) */}
      <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
      <Route path="/docs/*" element={<DocsRedirect />} />
      {/* Search analytics moved out of /settings so it never competes with /settings/:tab */}
      <Route path="/settings/search-analytics" element={<Navigate to="/search-analytics" replace />} />
      {/* Legacy screen, not redesigned yet */}
      <Route path="/graph" element={<RequireAuth><GraphPage /></RequireAuth>} />
      {/*
        App shell. <AppShell> renders the sidebar + topbar and an <Outlet/> for the child route.
        Project pages are children of /p/:projectSlug and read the project with
        useParams<{ projectSlug: string }>() + useProject(projectSlug) from hooks/use-projects; workspace pages
        get the current project from useCurrentProjectSlug(). Sidebar targets live in components/shell/nav-config.ts.
      */}
      <Route element={<RequireAuth><OnboardingGate><AppShell /></OnboardingGate></RequireAuth>}>
        <Route path="/" element={<DocumentsPage />} />
        <Route path="/doc/:slug" element={<DocumentRoute />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/settings" element={<SettingsIndexRedirect />} />
        <Route path="/settings/:tab" element={<SettingsPage />} />
        <Route path="/storage" element={<StoragePage />} />
        <Route path="/storage/:folderId" element={<StoragePage />} />
        <Route path="/sites" element={<HostedSitesPage />} />
        {/* Secrets moved to AgentKit's `ak secrets`; the old screen (routes/secrets.tsx) is kept in code, not routed */}
        <Route path="/secrets" element={<SecretsMovedPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/search-analytics" element={<SearchAnalyticsPage />} />
        <Route path="/p/:projectSlug">
          <Route index element={<ProjectOverviewPage />} />
          <Route path="wiki" element={<ProjectWikiPage />} />
          <Route path="wiki/:pageSlug" element={<ProjectWikiPageView />} />
          <Route path="sources" element={<ProjectSourcesPage />} />
          <Route path="sources/:sourceId" element={<ProjectSourceDetailPage />} />
          {/* The review queue is the "Waiting for the wiki" tab of Sources */}
          <Route path="review" element={<Navigate to="../sources?tab=review" replace />} />
          <Route path="review/:proposalId" element={<ProjectReviewDetailPage />} />
          <Route path="health" element={<ProjectHealthPage />} />
          <Route path="ask" element={<ProjectAskPage />} />
        </Route>
      </Route>
      <Route path="/p" element={<RequireAuth><ResumeProject /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

/**
 * A data router (one splat route around the existing <Routes> tree) so pages can use useBlocker,
 * e.g. the document editor asking before in-app navigation discards a draft.
 */
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <>
        <AppChrome />
        <AppRoutes />
      </>
    ),
  },
])

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  )
}
